import { useState } from 'react';
import { Card, Button, Popconfirm, Alert, Tag, Typography, message } from 'antd';
import { useQuery, useMutation } from '@tanstack/react-query';
import { mirrorOnboardingApi } from '../../api/mirrorOnboarding';
import type { MirrorOnboardingResult, OnboardingStep } from '../../api/mirrorOnboarding';

const STATUS_TAG: Record<OnboardingStep['status'], { color: string; label: string }> = {
  done:    { color: 'green',   label: '✓ yapıldı' },
  skipped: { color: 'blue',    label: '↷ atlandı' },
  failed:  { color: 'red',     label: '✗ hata' },
  not_run: { color: 'default', label: '— koşulmadı' },
};

export function MirrorOnboardingCard({ firmId, tenantId }: {
  firmId: number; tenantId?: string | null;
}) {
  const [result, setResult] = useState<MirrorOnboardingResult | null>(null);

  const { data: summary } = useQuery({
    queryKey: ['mirror-onboarding-summary', firmId],
    queryFn: () => mirrorOnboardingApi.summary(firmId),
    enabled: !!tenantId,
  });

  const run = useMutation({
    mutationFn: () => mirrorOnboardingApi.run(firmId),
    onSuccess: setResult,
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error ?? 'Mirror onboarding çağrısı başarısız'),
  });

  const confirmTitle = summary
    ? `Şablon: ${summary.templateFirmName} · ${summary.reportCount} rapor + ${summary.widgetCount} widget + ${summary.cdcTableCount} CDC tablosu klonlanacak`
    : 'Mirror kurulumu koşulacak';

  return (
    <Card title="Mirror Onboarding" style={{ marginTop: 24 }}>
      <Popconfirm
        title={confirmTitle}
        onConfirm={() => run.mutate()}
        okText="Evet"
        cancelText="Vazgeç"
        disabled={!tenantId}
      >
        <Button type="primary" disabled={!tenantId} loading={run.isPending}>
          Mirror kurulumunu çalıştır
        </Button>
      </Popconfirm>
      {!tenantId && (
        <Typography.Text type="secondary" style={{ marginLeft: 12 }}>
          önce Agent Kur (tenant bekleniyor)
        </Typography.Text>
      )}
      {result?.warnings.map(w => (
        <Alert key={w} type="warning" title={w} showIcon style={{ marginTop: 16 }} />
      ))}
      {result && (
        <div style={{ marginTop: 16 }}>
          {result.steps.map((step) => (
            <div key={step.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <Tag color={STATUS_TAG[step.status].color}>{STATUS_TAG[step.status].label}</Tag>
              <Typography.Text strong style={{ marginRight: 4 }}>{step.name}</Typography.Text>
              <Typography.Text type="secondary">{step.detail}</Typography.Text>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
